import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  createVoucherResolver,
  updateVoucherResolver,
  CreateVoucherFormData,
  UpdateVoucherFormData,
  createVoucherSchema,
  voucherBenefitStepSchema,
} from './schemas';
import { ChevronLeft, ChevronRight, Save, X } from 'lucide-react';
import { Voucher } from '@/services/admin/vouchers/vouchers.interfaces';

type VoucherFormCreateProps = {
  mode?: 'create';
  initialData?: undefined;
  onSubmit: (data: CreateVoucherFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
};

type VoucherFormEditProps = {
  mode: 'edit';
  initialData: Voucher;
  onSubmit: (data: UpdateVoucherFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
};

export type VoucherFormProps = VoucherFormCreateProps | VoucherFormEditProps;

const steps = [
  { id: 1, title: 'Detalhes Básicos', description: 'Código, nome e descrição' },
  { id: 2, title: 'Tipo e Benefício', description: 'Desconto ou tempo de acesso' },
  { id: 3, title: 'Limites e Validade', description: 'Limites de uso e período' },
  { id: 4, title: 'Restrições', description: 'Pacotes, categorias e produtos' },
  { id: 5, title: 'Revisão', description: 'Confirme os dados' },
];

export const VoucherForm: React.FC<VoucherFormProps> = ({
  mode = 'create',
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [currentStep, setCurrentStep] = useState(1);

  // Fields per step (used both for UI and error clearing)
  const stepFields: Array<(keyof CreateVoucherFormData)[]> = [
    ['code', 'name', 'description', 'isActive'], // 1
    ['voucherKind', 'discountType', 'discountValue', 'freeShipping', 'durationDays'], // 2
    ['maxUses', 'maxUsesPerUser', 'validFrom', 'validUntil', 'timezone'], // 3
    [], // 4 optional
    [], // 5 review
  ];

  // Step schemas: validate only visible fields
  const stepSchemas: z.ZodSchema<any>[] = [
    createVoucherSchema.pick({ code: true, name: true, description: true, isActive: true }),
    voucherBenefitStepSchema,
    createVoucherSchema.pick({
      maxUses: true,
      maxUsesPerUser: true,
      validFrom: true,
      validUntil: true,
      timezone: true,
    }),
    z.object({}), // no validation
    z.object({}), // review
  ];

  // Converter dados do voucher para formato do form
  const getDefaultValues = () => {
    if (mode === 'edit' && initialData) {
      return {
        code: initialData.code,
        name: initialData.name,
        description: initialData.description || '',
        isActive: initialData.isActive,
        voucherKind: initialData.voucherKind ?? 'discount',
        durationDays: initialData.durationDays,
        discountType: initialData.discountType,
        discountValue: initialData.discountValue,
        freeShipping: initialData.freeShipping || false,
        maxUses: initialData.maxUses,
        maxUsesPerUser: initialData.maxUsesPerUser,
        validFrom: initialData.validFrom,
        validUntil: initialData.validUntil,
        timezone: initialData.timezone || 'UTC',
        packageRestrictions: initialData.packageRestrictions,
        categoryRestrictions: initialData.categoryRestrictions,
        productRestrictions: initialData.productRestrictions,
      };
    }

    return {
      isActive: true,
      voucherKind: 'discount' as const,
      discountType: 'percentage' as const,
      discountValue: 0,
      timezone: 'UTC',
      freeShipping: false,
      ...initialData,
    };
  };

  const [formData, setFormData] = useState<Partial<CreateVoucherFormData>>(getDefaultValues());

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = useForm<CreateVoucherFormData>({
    resolver: mode === 'edit' ? updateVoucherResolver : createVoucherResolver,
    defaultValues: getDefaultValues(),
  });

  const discountType = watch('discountType');
  const voucherKind = watch('voucherKind') ?? 'discount';

  // Uppercase helper for code (matches schema regex)
  const toUpper = (s: string) => (s || '').toUpperCase();

  const handlePreviousStep = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleNextStep = async () => {
    const values = watch();
    const schema = stepSchemas[currentStep - 1];

    // normalize code before validating step 1
    if (currentStep === 1 && values.code) values.code = toUpper(values.code);

    // validate only current step schema
    const result = schema.safeParse(values);

    // clear previous errors for this step
    clearErrors(stepFields[currentStep - 1] as any);

    if (!result.success) {
      // surface issues to RHF so messages appear under each field
      for (const issue of result.error.issues) {
        const path = (issue.path?.[0] ?? '') as keyof CreateVoucherFormData;
        if (path) {
          setError(path, { type: 'manual', message: issue.message });
        }
      }
      return; // stay on this step
    }

    // persist for review
    setFormData((prev) => ({ ...prev, ...values }));

    if (currentStep < steps.length) setCurrentStep((s) => s + 1);
  };

  const handleFinalSubmit = (data: CreateVoucherFormData) => {
    // full schema already validated by resolver here
    onSubmit({ ...formData, ...data });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Código do Voucher *</label>
              <Controller
                control={control}
                name="code"
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Ex: WELCOME2024"
                    className={errors.code ? 'border-red-500' : ''}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(toUpper(e.target.value))}
                  />
                )}
              />
              {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Nome *</label>
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Ex: Welcome Discount 2024"
                    className={errors.name ? 'border-red-500' : ''}
                  />
                )}
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Descrição</label>
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <Textarea
                    {...field}
                    placeholder="Descrição opcional do voucher"
                    rows={3}
                    className={errors.description ? 'border-red-500' : ''}
                  />
                )}
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Controller
                control={control}
                name="isActive"
                render={({ field }) => <Switch checked={field.value} onCheckedChange={field.onChange} />}
              />
              <label className="text-sm font-medium">Voucher ativo</label>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Tipo de voucher *</label>
              <Controller
                control={control}
                name="voucherKind"
                render={({ field }) => (
                  <Select
                    value={field.value ?? 'discount'}
                    onValueChange={(value) => {
                      field.onChange(value);
                      if (value === 'discount') {
                        setValue('durationDays', undefined);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="discount">Desconto</SelectItem>
                      <SelectItem value="time">Tempo de acesso</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {voucherKind === 'time' ? (
              <div>
                <label className="block text-sm font-medium mb-1">Duração (dias) *</label>
                <Controller
                  control={control}
                  name="durationDays"
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      min="1"
                      placeholder="Ex: 30"
                      className={errors.durationDays ? 'border-red-500' : ''}
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                      }
                    />
                  )}
                />
                {errors.durationDays && (
                  <p className="text-red-500 text-sm mt-1">{errors.durationDays.message}</p>
                )}
                <p className="text-sm text-gray-500 mt-2">
                  Concede acesso ao pacote pelo período indicado, sem desconto no preço.
                </p>
              </div>
            ) : (
              <>
            <div>
              <label className="block text-sm font-medium mb-1">Tipo de Desconto *</label>
              <Controller
                control={control}
                name="discountType"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className={errors.discountType ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentual (%)</SelectItem>
                      <SelectItem value="fixedAmount">Valor Fixo (R$)</SelectItem>
                      <SelectItem value="freeShipping">Frete Grátis</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.discountType && (
                <p className="text-red-500 text-sm mt-1">{errors.discountType.message}</p>
              )}
            </div>

            {discountType !== 'freeShipping' && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  Valor do Desconto * {discountType === 'percentage' ? '(%)' : '(R$)'}
                </label>
                <Controller
                  control={control}
                  name="discountValue"
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      min="0"
                      max={discountType === 'percentage' ? 100 : undefined}
                      step={discountType === 'percentage' ? 1 : 0.01}
                      className={errors.discountValue ? 'border-red-500' : ''}
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                      }
                    />
                  )}
                />
                {errors.discountValue && (
                  <p className="text-red-500 text-sm mt-1">{errors.discountValue.message}</p>
                )}
              </div>
            )}

            {discountType === 'freeShipping' && (
              <div className="flex items-center space-x-2">
                <Controller
                  control={control}
                  name="freeShipping"
                  render={({ field }) => (
                    <Switch checked={field.value || false} onCheckedChange={field.onChange} />
                  )}
                />
                <label className="text-sm font-medium">Frete grátis habilitado</label>
              </div>
            )}
              </>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Uso Máximo Global</label>
                <Controller
                  control={control}
                  name="maxUses"
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      min="1"
                      placeholder="Ilimitado"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                    />
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Uso Máximo por Usuário</label>
                <Controller
                  control={control}
                  name="maxUsesPerUser"
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      min="1"
                      placeholder="Ilimitado"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                    />
                  )}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Data de Início *</label>
                <Controller
                  control={control}
                  name="validFrom"
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="datetime-local"
                      className={errors.validFrom ? 'border-red-500' : ''}
                    />
                  )}
                />
                {errors.validFrom && (
                  <p className="text-red-500 text-sm mt-1">{errors.validFrom.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Data de Fim *</label>
                <Controller
                  control={control}
                  name="validUntil"
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="datetime-local"
                      className={errors.validUntil ? 'border-red-500' : ''}
                    />
                  )}
                />
                {errors.validUntil && (
                  <p className="text-red-500 text-sm mt-1">{errors.validUntil.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Timezone</label>
              <Controller
                control={control}
                name="timezone"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UTC">UTC</SelectItem>
                      <SelectItem value="America/Sao_Paulo">America/Sao_Paulo</SelectItem>
                      <SelectItem value="America/New_York">America/New_York</SelectItem>
                      <SelectItem value="Europe/London">Europe/London</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="text-center py-8">
              <p className="text-gray-500">Configuração de restrições será implementada em breve</p>
              <p className="text-sm text-gray-400 mt-2">
                Por enquanto, o voucher será aplicável a todos os pacotes
              </p>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-3">Resumo do Voucher</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Código:</span>
                  <span className="font-mono">{formData.code}</span>
                </div>
                <div className="flex justify-between">
                  <span>Nome:</span>
                  <span>{formData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tipo:</span>
                  <span>{formData.voucherKind === 'time' ? 'Tempo de acesso' : 'Desconto'}</span>
                </div>
                {formData.voucherKind === 'time' ? (
                  <div className="flex justify-between">
                    <span>Duração:</span>
                    <span>{formData.durationDays} dias</span>
                  </div>
                ) : (
                <div className="flex justify-between">
                  <span>Desconto:</span>
                  <span>
                    {formData.discountType === 'percentage' && `${formData.discountValue}%`}
                    {formData.discountType === 'fixedAmount' && `R$ ${formData.discountValue}`}
                    {formData.discountType === 'freeShipping' && 'Frete Grátis'}
                  </span>
                </div>
                )}
                <div className="flex justify-between">
                  <span>Status:</span>
                  <Badge variant={formData.isActive ? 'default' : 'secondary'}>
                    {formData.isActive ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span>Válido até:</span>
                  <span>
                    {formData.validUntil ? new Date(formData.validUntil).toLocaleDateString() : '-'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{mode === 'edit' ? 'Editar Voucher' : 'Novo Voucher'}</span>
            <Button variant="ghost" size="sm" onClick={onCancel}>
              <X className="w-4 h-4" />
            </Button>
          </CardTitle>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-6">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                    currentStep >= step.id ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step.id}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`w-16 h-0.5 mx-2 ${
                      currentStep > step.id ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="text-center mb-4">
            <h3 className="font-medium">{steps[currentStep - 1].title}</h3>
            <p className="text-sm text-gray-500">{steps[currentStep - 1].description}</p>
          </div>
        </CardHeader>

        <CardContent>
          {/* prevent default; final submit is triggered manually */}
          <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
            {renderStepContent()}

            <div className="flex justify-between pt-4">
              <Button type="button" variant="outline" onClick={handlePreviousStep} disabled={currentStep === 1}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                Anterior
              </Button>

              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onCancel}>
                  Cancelar
                </Button>

                {currentStep < steps.length ? (
                  <Button type="button" onClick={handleNextStep} disabled={isLoading}>
                    Próximo
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button type="button" onClick={handleSubmit(handleFinalSubmit)} disabled={isLoading}>
                    <Save className="w-4 h-4 mr-2" />
                    {isLoading ? 'Salvando...' : mode === 'edit' ? 'Salvar Alterações' : 'Criar Voucher'}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
